import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";
import {
  UserPreferences,
  EvoFeedSubscriptions,
  EvoFeedSubscriptionKey,
  DEFAULT_USER_PREFERENCES
} from "../types/userPreferences";
import { fetchUserPreferences } from "../APIs/UserPreferences/GetUserPreferences";
import { saveUserPreferences } from "../APIs/UserPreferences/SaveUserPreferences";
import { errorHandler } from "../APIs/errorHandler";
import { CollinsUserContext } from "./CollinsUserContext";

export type UserPreferencesContextType = {
  preferences: UserPreferences;
  isLoading: boolean;
  isSaving: boolean;
  hasLoadError: boolean;
  loadPreferences: () => Promise<void>;
  updateEvoFeedSubscription: (
    key: EvoFeedSubscriptionKey,
    value: boolean
  ) => void;
  updateAndSaveSubscription: (
    key: EvoFeedSubscriptionKey,
    value: boolean
  ) => Promise<boolean>;
  savePreferences: () => Promise<boolean>;
};

export const UserPreferencesContext = createContext<UserPreferencesContextType>(
  {
    preferences: DEFAULT_USER_PREFERENCES,
    isLoading: false,
    isSaving: false,
    hasLoadError: false,
    loadPreferences: async () => {},
    updateEvoFeedSubscription: () => {},
    updateAndSaveSubscription: async () => false,
    savePreferences: async () => false
  }
);

export const UserPreferencesContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const { user: collinsUser } = useContext(CollinsUserContext);
  const [preferences, setPreferences] = useState<UserPreferences>(
    DEFAULT_USER_PREFERENCES
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  const loadPreferences = useCallback(async () => {
    setIsLoading(true);
    setHasLoadError(false);
    try {
      const userPreferences = await fetchUserPreferences(
        collinsUser?._id ?? ""
      );

      if (userPreferences) {
        setPreferences({
          ...DEFAULT_USER_PREFERENCES,
          ...userPreferences,
          evo_feed_subscriptions: {
            ...DEFAULT_USER_PREFERENCES.evo_feed_subscriptions,
            ...userPreferences.evo_feed_subscriptions
          }
        });
      }
    } catch (error) {
      setHasLoadError(true);
      errorHandler(error, "Failed to load user preferences");
    } finally {
      setIsLoading(false);
    }
  }, [collinsUser?._id]);

  const updateEvoFeedSubscription = useCallback(
    (key: EvoFeedSubscriptionKey, value: boolean) => {
      setPreferences((prev) => {
        const updatedSubscriptions: EvoFeedSubscriptions = {
          ...prev.evo_feed_subscriptions,
          [key]: value
        };

        // If subscribing to all auto-confirmed bookings,
        // force same-day auto-confirmed bookings to true as well
        if (key === "new_autoconfirmed_bookings" && value) {
          updatedSubscriptions.new_same_day_autoconfirmed_bookings = true;
        }

        return {
          ...prev,
          evo_feed_subscriptions: updatedSubscriptions
        };
      });
    },
    []
  );

  const savePreferences = useCallback(async (): Promise<boolean> => {
    setIsSaving(true);
    try {
      const savedPreferences = await saveUserPreferences(
        collinsUser?._id ?? "",
        preferences
      );
      setPreferences({
        ...DEFAULT_USER_PREFERENCES,
        ...savedPreferences,
        evo_feed_subscriptions: {
          ...DEFAULT_USER_PREFERENCES.evo_feed_subscriptions,
          ...savedPreferences.evo_feed_subscriptions
        }
      });
      return true;
    } catch (error) {
      errorHandler(error, "Failed to save user preferences");
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [collinsUser?._id, preferences]);

  const updateAndSaveSubscription = useCallback(
    async (key: EvoFeedSubscriptionKey, value: boolean): Promise<boolean> => {
      let previousPreferences: UserPreferences | undefined;

      const updatedPreferences = await new Promise<UserPreferences>(
        (resolve) => {
          setPreferences((prev) => {
            previousPreferences = prev;

            const updatedSubscriptions: EvoFeedSubscriptions = {
              ...prev.evo_feed_subscriptions,
              [key]: value
            };

            if (key === "new_autoconfirmed_bookings" && value) {
              updatedSubscriptions.new_same_day_autoconfirmed_bookings = true;
            }

            const next: UserPreferences = {
              ...prev,
              evo_feed_subscriptions: updatedSubscriptions
            };

            resolve(next);
            return next;
          });
        }
      );

      setIsSaving(true);

      try {
        const savedPreferences = await saveUserPreferences(
          collinsUser?._id ?? "",
          updatedPreferences
        );
        setPreferences({
          ...DEFAULT_USER_PREFERENCES,
          ...savedPreferences,
          evo_feed_subscriptions: {
            ...DEFAULT_USER_PREFERENCES.evo_feed_subscriptions,
            ...savedPreferences.evo_feed_subscriptions
          }
        });
        return true;
      } catch (error) {
        if (previousPreferences) {
          setPreferences(previousPreferences);
        }
        errorHandler(error, "Failed to save user preferences");
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [collinsUser?._id]
  );

  const contextValue = useMemo(
    () => ({
      preferences,
      isLoading,
      isSaving,
      hasLoadError,
      loadPreferences,
      updateEvoFeedSubscription,
      updateAndSaveSubscription,
      savePreferences
    }),
    [
      preferences,
      isLoading,
      isSaving,
      hasLoadError,
      loadPreferences,
      updateEvoFeedSubscription,
      updateAndSaveSubscription,
      savePreferences
    ]
  );

  return (
    <UserPreferencesContext.Provider value={contextValue}>
      {children}
    </UserPreferencesContext.Provider>
  );
};
